import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { getHeaderData, getMenus, getSubmenus, getallpage } from "../Api/api";
import { isAuthenticated, logout, getMe } from "../Api/auth";

const Header = () => {
  const [headerCfg, setHeaderCfg] = useState({ logo_url: null, color1: "#fff", color2: "#93D52D" });
  const [dynamicMenus, setDynamicMenus] = useState([]);
  const [loading, setLoading] = useState(true);

  const { lang, setLang } = useLanguage();
  const { t, i18n } = useTranslation();

  // لإضافة shadow عند النزول بالصفحة
  const [scrolled, setScrolled] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ✅ تبديل اللغة بزر واحد فقط
  const toggleLanguage = () => {
    const next = lang === "ar" ? "en" : "ar";
    setLang(next);
    i18n.changeLanguage(next);
  };

  const normalizeLogoUrl = (url) => {
    const s = String(url || "").trim();
    if (!s) return null;
    let out = s;
    if (out.includes("/public_html/")) {
      out = out.replace("/public_html/", "/via_api/");
    }
    return out;
  };

  const closeMobileMenu = () => {
    try {
      const toggle = document.querySelector(".navbar-toggler");
      const collapse = document.getElementById("mainNavbar");
      if (collapse && collapse.classList.contains("show") && toggle) {
        toggle.click();
      }
    } catch (e) {
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const langId = i18n.language === "ar" ? 2 : 1;
        const [headerRes, menusRes, submenusRes, pagesRes] = await Promise.all([
          getHeaderData().catch(() => ({ header: null })),
          getMenus().catch(() => ({ data: [] })),
          getSubmenus(langId).catch(() => ({ data: [] })),
          getallpage(langId).catch(() => ({ data: [] })),
        ]);
        const h = headerRes?.header || headerRes || {};
        setHeaderCfg({
          logo_url: normalizeLogoUrl(h.logo_url) || null,
          color1: h.color1 || "#fff",
          color2: h.color2 || "#93D52D",
        });
        const menus = menusRes.data || menusRes || [];
        const subs = submenusRes.data || submenusRes || [];
        const pagesRaw = pagesRes.data || pagesRes || [];
        const pages = Array.isArray(pagesRaw) ? pagesRaw.filter((p) => Number(p?.is_active ?? 1) === 1) : [];

        if (Array.isArray(menus) && Array.isArray(subs) && Array.isArray(pages)) {
          const built = menus
            .map((menu) => {
              const menuSubmenusRaw = subs
                .filter((s) => s.menu_id === menu.id)
                .sort((a, b) => a.order_index - b.order_index)
                .map((sub) => ({
                  ...sub,
                  pages: pages.filter((p) => p.submenu_id === sub.id),
                }));
              const menuSubmenus = menuSubmenusRaw.filter((sub) => Array.isArray(sub.pages) && sub.pages.length > 0);

              if (menuSubmenus.length === 0) return null;
              return {
                id: menu.id,
                order_index: menu.order_index,
                name: menuSubmenus[0].name,
                submenus: menuSubmenus,
              };
            })
            .filter(Boolean)
            .sort((a, b) => Number(a.order_index) - Number(b.order_index));
          setDynamicMenus(built);
        }
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [i18n.language]);

  useEffect(() => {
    const initAuth = async () => {
      const ok = isAuthenticated();
      setAuthed(ok);
      if (ok) {
        const res = await getMe();
        if (res?.success) setUser(res.user);
      }
    };
    initAuth();
  }, []);

  useEffect(() => {
    const ok = isAuthenticated();
    setAuthed(ok);
    if (ok && !user) {
      getMe().then((res) => { if (res?.success) setUser(res.user); });
    }
  }, [location]);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      setAuthed(false);
      setUser(null);
      closeMobileMenu();
      navigate("/");
    }
  };

  return (
    <header dir={i18n.language === "ar" ? "ltr" : "ltr"}>
      <nav
        className={`navbar navbar-expand-lg navbar-light fixed-top ${scrolled ? "shadow-sm" : ""}`}
        style={{
          background: `linear-gradient(90deg, ${headerCfg.color1} 0%, ${headerCfg.color2} 100%)`,

          backdropFilter: "saturate(180%) blur(10px)",
          transition: "all 0.3s ease-in-out",
          boxShadow: scrolled ? "0 6px 20px rgba(0,0,0,0.08)" : "none"
        }}
      >
        <div className="container">
          {/* Logo */}
          <NavLink className="navbar-brand" to="/" onClick={closeMobileMenu}>
            <img
              src={headerCfg.logo_url || require("../Images/logo.png")}
              alt="Logo"
              style={{ width: "120px" ,maxWidth:"120px", height: "auto",maxHeight:"40px" }}
            />
          </NavLink>

          {/* Toggle for Mobile */}
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#mainNavbar"
            aria-controls="mainNavbar"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          {/* Links */}
          <div className="collapse navbar-collapse" id="mainNavbar">
            <ul className="navbar-nav ms-auto  mb-lg-0 align-items-center">
              {authed && (
                <li className="nav-item">
                  <NavLink
                    to="/dashboard"
                    className={({ isActive }) =>
                      `nav-link custom-link fs-5 me-3 ${isActive ? "active-link" : ""}`
                    }
                    onClick={closeMobileMenu}
                  >
                    {i18n.language === "en" ? "Dashboard" : "Dashboard"}
                  </NavLink>
                </li>
              )}

              {!loading && dynamicMenus.map((menu) => {
                const totalPages = menu.submenus.reduce((sum, sub) => sum + sub.pages.length, 0);
                if (totalPages === 0) {
                  const n = String(menu.name || "").toLowerCase();
                  if (n === "home" || n === "home") {
                    return (
                      <li key={menu.id} className="nav-item">
                        <NavLink
                          to={`/`}
                          className={({ isActive }) =>
                            `nav-link custom-link fs-5 me-3 ${isActive ? "active-link" : ""}`
                          }
                          onClick={closeMobileMenu}
                        >
                          {t("nav.home")}
                        </NavLink>
                      </li>
                    );
                  }
                }
                if (totalPages === 1) {
                  const page = menu.submenus[0].pages[0];
                  return (
                    <li key={menu.id} className="nav-item">
                      <NavLink
                        to={`/${page.slug}`}
                        className={({ isActive }) =>
                          `nav-link custom-link fs-5 me-3 ${isActive ? "active-link" : ""}`
                        }
                        onClick={closeMobileMenu}
                      >
                        {menu.name}
                      </NavLink>
                    </li>
                  );
                }
                const ddId = `${String(menu.name || "").toLowerCase()}Dropdown`;
                return (
                  <li key={menu.id} className="nav-item dropdown">
                    <span
                      className="nav-link custom-links  fs-5 me-3"
                      id={ddId}
                      role="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {menu.name}
                    </span>
                    <ul className="dropdown-menu" aria-labelledby={ddId}>
                      {menu.submenus.map((submenu) =>
                         submenu.pages.map((page) => (
                            <li key={page.id}>
                              <NavLink className="dropdown-item" to={`/${page.slug}`} onClick={closeMobileMenu}>
                                {page.title || page.slug.charAt(0).toUpperCase() + page.slug.slice(1).replace(/-/g, " ")}
                              </NavLink>
                            </li>
                          ))
                      )}
                      {menu.submenus.every((sub) => sub.pages.length === 0) && (
                        <li className="dropdown-item text-muted">No pages</li>
                      )}
                    </ul>
                  </li>
                );
              })}

              <div className="buttons-wrap d-flex flex-row flex-nowrap align-items-center gap-2 ms-lg-3 py-3">
                {/* ✅ زر تبديل اللغة */}
                <motion.button
                  className="btn bho ms-3" style={{ border: "1.5px solid #1B5772", color: "#1B5772" ,fontSize: '14px'}}
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  onClick={toggleLanguage}
                >
                  {i18n.language === "en" ? "French" : "English"}
                </motion.button>

                {authed && (
                  <motion.button
                    className="btn bho ms-3" style={{ border: "1.5px solid #1B5772", color: "#1B5772" ,fontSize: '14px' }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    onClick={handleLogout}
                  >
                    Logout
                  </motion.button>
                )}

              </div>
            </ul>
          </div>
        </div>
      </nav>

      {/* Custom CSS */}
      <style>{`
        .navbar {
          z-index: 1000;
          backdrop-filter: saturate(180%) blur(6px);
          border-bottom: 1px solid rgba(27, 87, 115, 0.08);
        }
        .navbar-brand {
          color: #1b5773 !important;
          margin-inline-end: 10px; 
        }

        .custom-link {
          position: relative;
          color: #1b5773 !important;
          font-weight: 500;
          transition: all 0.3s ease-in-out;
        }
        .custom-links {
          position: relative;
          color: #1b5773 !important;
          font-weight: 500;
          transition: all 0.3s ease-in-out;
        }

        .custom-link::after {
          content: "";
          position: absolute;
          width: 0%;
          height: 3px;
          left: 0;
          bottom: 0;
          background-color: #1b5773;
          transition: width 0.3s ease-in-out;
        }

        .custom-link:hover {
          color: #16495f !important;
        }

        .custom-link:hover::after {
          width: 100%;
        }

  .active-link {
    color: #16495f !important;
    font-weight: 600;
  }

  .active-link::after {
    width: 100%;
  }

        .navbar-toggler {
          border: none;
        }
        .navbar-toggler:focus {
          box-shadow: none;
        }

        /* فتح القائمة عند hover على الديسكتوب */
        @media (min-width: 992px) {
          .nav-item.dropdown:hover .dropdown-menu {
            display: block;
            margin-top: 0;
          }
        }

        .dropdown-menu {
          border-radius: 8px;
          overflow: hidden;
          border: none;
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          padding: 8px 0;
        }

        .dropdown-item {
          color: #1b5773;
          transition: all 0.3s ease-in-out;
        }

        .dropdown-item:hover {
          background: #1b5773;
          color: #fff;
        }
          .btn{
          border-radius: 999px;
          padding: 8px 16px;
          }
          .custom-link::after {
  height: 2px;
  border-radius: 4px;
}

.bho{
  background: transparent;
}
.bho:hover{
  color: #fff !important;
   background: #1b5773;
}

      `}</style>
    </header>
  );
};

//  .btn{
//       border-radius: 999px;
//       padding: 8px 16px;
//       background: transparent;
//       }
//       .btn:hover{
//       color: #fff !important;
//        background: #1b5773;
//       }
export default Header;